import {Input, MultiSelect, Select, Stack, Text} from '@mantine/core';
import React, {useEffect, useMemo} from 'react';

import {LABELING_STRATEGY_TO_TEXT, RegistryLabelingStrategy} from 'constants/RegistryConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {getDefaultLabelingStrategy} from 'registry/labeling-strategies/LabelingStrategiesUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import FieldToColumnMapper, {DatasetColumn} from 'spreadsheet-import-wizard/shared/FieldToColumnMapper';
import {JSONValue} from 'util/Types';

import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../../StepConfigEditorContext';
import {CustomComponentEditorProps} from '../../StepConfigEditorTypes';
import {useCreateEntitiesEntitySchemas} from './CreateEntitiesEditorHelpers';

const INPUT_DATA_NAME = 'Input dataset';

interface CreateEntitiesConfig {
  schemaId?: string | null;
  folderId?: string | null;
  namingStrategy?: string | null;
  registryId?: string | null;
  nameColumn?: string | null;
  aliasColumns?: Array<string> | string | null;
  columnMapping?: Record<string, string> | null;
}

function getAliasColumns(aliasColumns: CreateEntitiesConfig['aliasColumns']): Array<string> {
  if (Array.isArray(aliasColumns)) {
    return aliasColumns.filter((column): column is string => typeof column === 'string' && column !== '');
  }
  return aliasColumns ? [aliasColumns] : [];
}

function getSchemaFields(fieldsJson: unknown): Array<SchemaField> {
  return Array.isArray(fieldsJson) ? (fieldsJson as Array<SchemaField>) : [];
}

export default function CreateEntitiesEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const typedConfig = config as CreateEntitiesConfig;

  const tagSchemas = useCreateEntitiesEntitySchemas();

  const {dataFramesInfos} = useStepConfigEditorContext();
  const inputColumns = getDataFrameInfoOrEmptyInfo(dataFramesInfos, INPUT_DATA_NAME).dataFrameSpec.columns;

  const columnMapping = useMemo(() => typedConfig.columnMapping ?? {}, [typedConfig.columnMapping]);

  const selectedSchema = useMemo(
    () => tagSchemas?.find((schema) => schema.id === typedConfig.schemaId) ?? null,
    [tagSchemas, typedConfig.schemaId]
  );

  const schemaFields = useMemo(
    () => getSchemaFields(selectedSchema?.fieldsJson).filter((field) => !field.isComputed),
    [selectedSchema]
  );

  // Field mappings are persisted as schemaFieldSystemName -> datasetColumnSystemName, the shape
  // BulkIngest resolves fields by. Both the mapper rows and the persisted keys use systemName.
  const fieldRows = useMemo(
    () => schemaFields.map((field) => ({id: field.systemName, displayName: field.name})),
    [schemaFields]
  );
  const columnOptions: Array<DatasetColumn> = useMemo(
    () =>
      inputColumns.map((column) => ({
        id: column.systemName,
        displayName: column.displayName,
      })),
    [inputColumns]
  );

  const columnSelectData = useMemo(
    () => columnOptions.map((column) => ({value: column.id, label: column.displayName})),
    [columnOptions]
  );

  // Naming strategies come from the selected schema's own `labelingStrategies`, not a hardcoded set.
  const schemaLabelingStrategies = useMemo<Array<RegistryLabelingStrategy>>(
    () => selectedSchema?.labelingStrategies ?? [],
    [selectedSchema]
  );

  const namingStrategyOptions = useMemo(
    () =>
      schemaLabelingStrategies.map((strategy) => ({
        value: strategy,
        label: LABELING_STRATEGY_TO_TEXT[strategy] ?? strategy,
      })),
    [schemaLabelingStrategies]
  );

  const setConfigValue = (path: Array<string>, value: JSONValue): void => {
    onChangeConfig(config, path, value);
  };

  // Pre-select the schema's default labeling strategy once a schema is chosen and none is set yet.
  useEffect(() => {
    if (disabled || typedConfig.namingStrategy != null || schemaLabelingStrategies.length === 0) {
      return;
    }
    onChangeConfig(
      config,
      ['namingStrategy'],
      getDefaultLabelingStrategy(schemaLabelingStrategies, selectedSchema ? [selectedSchema.id] : undefined)
    );
  }, [
    config,
    onChangeConfig,
    disabled,
    typedConfig.namingStrategy,
    schemaLabelingStrategies,
    selectedSchema,
  ]);

  const handleSchemaChange = (schemaId: string | null): void => {
    // Changing schema invalidates field mappings, so clear them. Naming strategy is per-schema, so
    // reset it -- the default-selection effect repopulates it.
    onChangeConfig(config, [], {...config, schemaId, columnMapping: {}, namingStrategy: null});
  };

  const handleFieldMappingsChange = (fieldMappings: Record<string, string>): void => {
    setConfigValue(['columnMapping'], fieldMappings);
  };

  return (
    <Stack spacing="md">
      <div>
        <Input.Label required={true}>Schema</Input.Label>
        <Select
          searchable={true}
          clearable={true}
          placeholder="Search for an entity schema..."
          value={typedConfig.schemaId ?? null}
          disabled={disabled || tagSchemas == null}
          nothingFound="No schemas found"
          data={(tagSchemas ?? []).map((schema) => ({value: schema.id, label: schema.name}))}
          onChange={handleSchemaChange}
        />
      </div>

      <div>
        <Input.Label required={true}>Folder</Input.Label>
        <FolderSelector
          value={typedConfig.folderId ?? null}
          allowNone={false}
          showRegistries={true}
          enableRedesign={true}
          level="append"
          error=""
          readOnly={Boolean(disabled)}
          requireSubfolderCreatableAuth={false}
          showInventory={true}
          showNotebook={false}
          unsetOnFetchFail={false}
          checkExistingItemForDataModsMovement={false}
          noFolderText="Choose folder"
          onChange={(e) => {
            setConfigValue(['folderId'], e.target.value ?? null);
          }}
        />
      </div>

      <div>
        <Input.Label required={true}>Naming strategy</Input.Label>
        <Select
          placeholder={selectedSchema == null ? 'Select a schema first...' : 'Select a naming strategy...'}
          value={typedConfig.namingStrategy ?? null}
          disabled={disabled || selectedSchema == null}
          nothingFound="No naming strategies available for this schema"
          data={namingStrategyOptions}
          onChange={(value) => {
            setConfigValue(['namingStrategy'], value);
          }}
        />
      </div>

      <div>
        <Input.Label>Name column</Input.Label>
        <Select
          searchable={true}
          clearable={true}
          placeholder="Select the dataset column for entity names..."
          value={typedConfig.nameColumn ?? null}
          disabled={disabled}
          nothingFound="No columns available"
          data={columnSelectData}
          onChange={(value) => {
            setConfigValue(['nameColumn'], value ?? null);
          }}
        />
      </div>

      <div>
        <Input.Label>Alias columns</Input.Label>
        <MultiSelect
          searchable={true}
          clearable={true}
          placeholder="Select dataset columns for aliases (optional)..."
          value={getAliasColumns(typedConfig.aliasColumns)}
          disabled={disabled}
          nothingFound="No columns available"
          data={columnSelectData}
          onChange={(value) => {
            setConfigValue(['aliasColumns'], value.length > 0 ? value : null);
          }}
        />
      </div>

      <div>
        <Input.Label>Field mappings</Input.Label>
        {selectedSchema == null ? (
          <Text color="dimmed" size="sm">
            Select a schema to map its fields to dataset columns.
          </Text>
        ) : fieldRows.length === 0 ? (
          <Text color="dimmed" size="sm">
            This schema has no fields to map.
          </Text>
        ) : (
          <FieldToColumnMapper
            rows={fieldRows}
            columns={columnOptions}
            mappings={columnMapping}
            onMappingChange={handleFieldMappingsChange}
            disabled={disabled}
          />
        )}
      </div>
    </Stack>
  );
}
